import React, { useState } from "react";
import { Modal, Form, Alert, Button as BSButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTimes, faSave } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "components/base/Button";
import { useEmpresas } from "providers/EmpresaProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { balancoService, AtualizarProdutoBalancoDto } from "services/balancoService";
import { balancoKeys } from "hooks/useBalanco";

interface BotaoEditarProdutoProps {
    produto: {
        produtoId: number;
        nomeProduto: string;
        lote?: string;
        quantidade: number;
        controleEspecial?: boolean;
    };
    disabled?: boolean;
    size?: "sm" | "lg";
}

const BotaoEditarProduto: React.FC<BotaoEditarProdutoProps> = ({
    produto,
    disabled = false,
    size = "sm"
}) => {
    const [showModal, setShowModal] = useState(false);
    const [quantidade, setQuantidade] = useState(produto.quantidade.toString());

    // Hooks
    const queryClient = useQueryClient();
    const { selectedIds } = useEmpresas();
    const { getMutationErrorHandler } = usePermissionModal();
    const empresaId = selectedIds[0];

    // Mutation para atualizar produto
    const atualizarProdutoMutation = useMutation({
        mutationFn: (dto: AtualizarProdutoBalancoDto) =>
            balancoService.atualizarProduto(dto, empresaId),
        onSuccess: () => {
            setShowModal(false);
            // Invalidar cache do balanço
            queryClient.invalidateQueries({ queryKey: balancoKeys.ativo() });
        },
        onError: getMutationErrorHandler("Erro ao atualizar produto no balanço.")
    });

    const handleSalvar = () => {
        const novaQuantidade = parseFloat(quantidade);

        if (isNaN(novaQuantidade) || novaQuantidade <= 0) {
            return;
        }

        const dto: AtualizarProdutoBalancoDto = {
            produtoId: produto.produtoId,
            lote: produto.lote,
            novaQuantidade
        };

        atualizarProdutoMutation.mutate(dto);
    };

    const handleOpenModal = () => {
        setQuantidade(produto.quantidade.toString());
        setShowModal(true);
    };

    return (
        <>
            <Button
                variant="outline-primary"
                size={size}
                onClick={handleOpenModal}
                disabled={disabled}
                title="Editar produto"
            >
                <FontAwesomeIcon icon={faEdit} />
            </Button>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faEdit} className="me-2 text-primary-emphasis" />
                        Editar Produto no Balanço
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <strong>{produto.nomeProduto}</strong>
                        {produto.lote && (
                            <div className="text-body-secondary small">
                                Lote: <span className="badge bg-secondary">{produto.lote}</span>
                            </div>
                        )}
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Quantidade *</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={quantidade}
                            onChange={(e) => setQuantidade(e.target.value)}
                            placeholder="Digite a nova quantidade"
                            isInvalid={isNaN(parseFloat(quantidade)) || parseFloat(quantidade) <= 0}
                        />
                        <Form.Control.Feedback type="invalid">
                            A quantidade deve ser um número maior que zero
                        </Form.Control.Feedback>
                    </Form.Group>

                    {atualizarProdutoMutation.isError && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">
                                <strong>Erro:</strong> {(atualizarProdutoMutation.error as Error).message}
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <BSButton
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={atualizarProdutoMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Cancelar
                    </BSButton>
                    <BSButton
                        variant="primary"
                        onClick={handleSalvar}
                        disabled={
                            atualizarProdutoMutation.isPending ||
                            isNaN(parseFloat(quantidade)) ||
                            parseFloat(quantidade) <= 0
                        }
                    >
                        {atualizarProdutoMutation.isPending ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                Salvar
                            </>
                        )}
                    </BSButton>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BotaoEditarProduto; 